<template>
  <div>

    <div class="d-flex align-center mb-2">
      <v-btn color="silver" depressed :disabled="loadState.isLoading" small @click="formOpen()">
        <v-icon class="pr-2">mdi-plus</v-icon>Добавить ссылку
      </v-btn>
      <v-spacer/>
      <v-checkbox
          v-model="showDeleted"
          label="Показывать удалённые"
          class="mt-0"
          dense hide-details
          @change="load"
      />
    </div>

    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loadState.isLoading"
        :items-per-page="-1"
        :item-class="rowClass"
        hide-default-footer
        no-data-text="Для специалиста пока не добавлены ссылки для отзывов"
        loading-text="Загрузка..."
        dense
    >
      <template v-slot:item.resource="{item}">
        {{ resourceTitle(item) }}
      </template>
      <template v-slot:item.comBranch="{item}">
        <span v-if="item.comBranch!=null">{{ item.comBranch.name }}</span>
        <span v-else class="text--secondary">Все</span>
      </template>
      <template v-slot:item.targetUrl="{item}">
        <a :href="item.targetUrl" target="_blank" rel="noopener" class="d-inline-block text-truncate" style="max-width:220px">{{ item.targetUrl }}</a>
      </template>
      <template v-slot:item.isDefault="{item}">
        <v-icon v-if="item.isDefault" color="primary" small>mdi-check-circle</v-icon>
        <span v-else class="text--secondary">—</span>
      </template>
      <template v-slot:item.showInMedRecord="{item}">
        <v-icon v-if="item.showInMedRecord" color="success" small>mdi-check</v-icon>
        <span v-else class="text--secondary">—</span>
      </template>
      <template v-slot:item.showInMedia="{item}">
        <v-icon v-if="item.showInMedia" color="success" small>mdi-check</v-icon>
        <span v-else class="text--secondary">—</span>
      </template>
      <template v-slot:item.actions="{item}">
        <v-btn icon small title="Открыть публичную страницу QR" @click="openPublic(item)">
          <v-icon small>mdi-qrcode</v-icon>
        </v-btn>
        <v-btn icon small title="Открыть внешнюю ссылку" @click="openUrl(item.targetUrl)">
          <v-icon small>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn v-if="item.status==='active'" icon small title="Редактировать" @click="formOpen(item)">
          <v-icon small>mdi-pencil-outline</v-icon>
        </v-btn>
        <btn-small-icon-accept
            v-if="item.status==='active'"
            icon="mdi-trash-can-outline"
            btn-class="error--text"
            text="Удалить ссылку? Запись будет скрыта, но не удалена физически, чтобы уже созданные QR-коды продолжали работать."
            :loading="deletingId===item.id"
            @accept="deleteAction(item)"
        />
        <v-btn v-else icon small title="Восстановить" @click="restore(item)">
          <v-icon small color="primary">mdi-restore</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Форма создания/редактирования -->
    <v-dialog v-model="form.isOpen" max-width="640" persistent>
      <v-card>
        <v-card-title>{{ form.id==null ? 'Новая ссылка для отзывов' : 'Редактирование ссылки' }}</v-card-title>
        <v-card-text>
          <v-form ref="reviewLinkForm" v-model="form.valid">
            <v-row class="pt-2">
              <v-col cols="12" sm="6">
                <v-select
                    v-model="form.data.resourceCode"
                    :items="resourceItems"
                    item-text="name"
                    item-value="code"
                    label="Ресурс"
                    :rules="[v => !!v || 'Выберите ресурс']"
                    outlined dense hide-details="auto"
                    @change="onResourceChange"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                    v-model="form.data.resourceName"
                    label="Название ресурса"
                    :disabled="form.data.resourceCode!=='other'"
                    :rules="resourceNameRules"
                    counter="128"
                    maxlength="128"
                    outlined dense hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                    v-model="form.data.targetUrl"
                    label="Ссылка"
                    placeholder="https://..."
                    :rules="urlRules"
                    outlined dense hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <com-branch-autocomplete-single :select.sync="form.data.comBranch" class="mb-0"/>
                <div class="caption text--secondary mt-1">Не выбран — ссылка действует для всех филиалов</div>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                    v-model.number="form.data.sort"
                    type="number"
                    min="0"
                    label="Порядок"
                    hint="Чем меньше число, тем выше ссылка"
                    :rules="[v => (v!==null && v!=='' && Number(v)>=0) || 'Минимум 0']"
                    outlined dense hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox v-model="form.data.isDefault" label="Основная ссылка" class="mt-0" dense hide-details/>
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox v-model="form.data.showInMedRecord" label="В мед. записях" class="mt-0" dense hide-details/>
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox v-model="form.data.showInMedia" label="В документах" class="mt-0" dense hide-details/>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="saveState.isLoading" @click="form.isOpen=false">Отмена</v-btn>
          <v-btn color="primary" text :loading="saveState.isLoading" @click="save">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import Api from "@/Api";
import State from "@/plugins/state";
import ComBranchAutocompleteSingle from "@/componentsV2/custom/ComBranchAutocompleteSingle.vue";
import BtnSmallIconAccept from "@/componentsV2/base/BtnSmallIconAccept.vue";
import {
  Entity_UserReviewLink,
  Entity_ComBranch,
  Entity_User,
} from "../../../EntityStoreCacheService";

export default {
  components: {ComBranchAutocompleteSingle, BtnSmallIconAccept},
  props: ['userId'],
  data: () => ({
    loadState: new State(),
    saveState: new State(),
    deletingId: null,
    items: [],
    showDeleted: false,
    resources: {},
    headers: [
      {text: 'Ресурс', value: 'resource', sortable: false},
      {text: 'Филиал', value: 'comBranch', sortable: false},
      {text: 'Ссылка', value: 'targetUrl', sortable: false},
      {text: 'Основная', value: 'isDefault', align: 'center', sortable: false},
      {text: 'MedRecord', value: 'showInMedRecord', align: 'center', sortable: false},
      {text: 'Media', value: 'showInMedia', align: 'center', sortable: false},
      {text: 'Порядок', value: 'sort', align: 'center'},
      {text: 'Действия', value: 'actions', align: 'right', sortable: false, cellClass: 'text-no-wrap'},
    ],
    form: {
      isOpen: false,
      valid: true,
      id: null,
      data: {
        resourceCode: null,
        resourceName: '',
        targetUrl: '',
        comBranch: null,
        sort: 0,
        isDefault: false,
        showInMedRecord: true,
        showInMedia: true,
      },
    },
  }),
  computed: {
    resourceItems() {
      return Object.keys(this.resources).map(code => ({code: code, name: this.resources[code]}));
    },
    urlRules() {
      return [
        v => !!v || 'Укажите ссылку',
        v => /^https?:\/\//i.test(v || '') || 'Ссылка должна начинаться с http:// или https://',
      ];
    },
    resourceNameRules() {
      return [
        v => this.form.data.resourceCode !== 'other' || !!v || 'Укажите название ресурса',
        v => (v || '').length <= 128 || 'Не более 128 символов',
      ];
    },
  },
  mounted() {
    this.resourcesLoad();
    this.load();
  },
  methods: {
    rowClass(item) {
      return item.status === 'deleted' ? 'isInactive' : '';
    },
    resourceTitle(item) {
      return item.resourceName || this.resources[item.resourceCode] || item.resourceCode;
    },
    resourcesLoad() {
      axios.get(Api.host + '/user/' + this.userId + '/reviewLink/')
          .then((r) => {
            this.resources = r.data.resources || {};
          })
          .catch((e) => {
            console.error(e);
          });
    },
    load() {
      let rb = new Entity_UserReviewLink()
          .selects(Entity_UserReviewLink.id)
          .selects(Entity_UserReviewLink.resourceCode)
          .selects(Entity_UserReviewLink.resourceName)
          .selects(Entity_UserReviewLink.targetUrl)
          .selects(Entity_UserReviewLink.publicToken)
          .selects(Entity_UserReviewLink.status)
          .selects(Entity_UserReviewLink.sort)
          .selects(Entity_UserReviewLink.isDefault)
          .selects(Entity_UserReviewLink.showInMedRecord)
          .selects(Entity_UserReviewLink.showInMedia)
          .selects(Entity_UserReviewLink.created)
          .selects(Entity_UserReviewLink.updated)
          .selects([Entity_UserReviewLink.comBranch, Entity_ComBranch.id].join('.'))
          .selects([Entity_UserReviewLink.comBranch, Entity_ComBranch.name].join('.'))
          .filterAnd([Entity_UserReviewLink.entity, Entity_UserReviewLink.user, Entity_User.id].join('.') + '=?', parseInt(this.userId));

      if (!this.showDeleted)
        rb.filterAnd([Entity_UserReviewLink.entity, Entity_UserReviewLink.status].join('.') + '=?', 'active');

      rb.order(Entity_UserReviewLink.sort, true)
          .order(Entity_UserReviewLink.id, true)
          .page(null)
          .addStateCallback((v) => this.loadState.setRequestBuilderState(v))
          .request((r) => {
            this.items = r.items;
          }, (e) => {
            this.errorAlert(e);
          });
    },
    formOpen(item = null) {
      if (item == null) {
        this.form.id = null;
        this.form.data = {
          resourceCode: null,
          resourceName: '',
          targetUrl: '',
          comBranch: null,
          sort: 0,
          isDefault: false,
          showInMedRecord: true,
          showInMedia: true,
        };
      } else {
        this.form.id = item.id;
        this.form.data = {
          resourceCode: item.resourceCode,
          resourceName: item.resourceName || '',
          targetUrl: item.targetUrl,
          comBranch: item.comBranch != null ? {...item.comBranch} : null,
          sort: item.sort,
          isDefault: !!item.isDefault,
          showInMedRecord: !!item.showInMedRecord,
          showInMedia: !!item.showInMedia,
        };
      }
      this.form.isOpen = true;
      this.$nextTick(() => {
        if (this.$refs.reviewLinkForm) this.$refs.reviewLinkForm.resetValidation();
      });
    },
    onResourceChange(code) {
      if (code !== 'other')
        this.form.data.resourceName = this.resources[code] || '';
    },
    save() {
      if (!this.$refs.reviewLinkForm.validate()) return;

      let data = {
        resourceCode: this.form.data.resourceCode,
        resourceName: this.form.data.resourceCode === 'other' ? this.form.data.resourceName : '',
        targetUrl: this.form.data.targetUrl,
        comBranchId: this.form.data.comBranch != null ? this.form.data.comBranch.id : null,
        sort: Number(this.form.data.sort) || 0,
        isDefault: this.form.data.isDefault,
        showInMedRecord: this.form.data.showInMedRecord,
        showInMedia: this.form.data.showInMedia,
      };

      this.saveState.stateLoading();
      let request = this.form.id == null
          ? axios.post(Api.host + '/user/' + this.userId + '/reviewLink/', data)
          : axios.put(Api.host + '/userReviewLink/' + this.form.id + '/', data);

      request
          .then(() => {
            this.saveState.stateSuccess();
            this.form.isOpen = false;
            this.$store.commit('alertSuccess', this.form.id == null ? 'Ссылка добавлена' : 'Ссылка обновлена');
            this.load();
          })
          .catch((e) => {
            this.saveState.stateError();
            this.errorAlert(e);
          });
    },
    deleteAction(item) {
      this.deletingId = item.id;
      axios.delete(Api.host + '/userReviewLink/' + item.id + '/')
          .then(() => {
            this.deletingId = null;
            this.$store.commit('alertSuccess', 'Ссылка удалена');
            this.load();
          })
          .catch((e) => {
            this.deletingId = null;
            this.errorAlert(e);
          });
    },
    restore(item) {
      axios.put(Api.host + '/userReviewLink/' + item.id + '/', {status: 'active'})
          .then(() => {
            this.$store.commit('alertSuccess', 'Ссылка восстановлена');
            this.load();
          })
          .catch((e) => {
            this.errorAlert(e);
          });
    },
    openPublic(item) {
      if (this.$tools.isEmpty(item.publicToken)) {
        this.$store.commit('alertWarning', 'Публичный токен ещё не сформирован');
        return;
      }
      window.open(location.origin + '/review/' + item.publicToken, '_blank');
    },
    openUrl(url) {
      window.open(url, '_blank');
    },
    errorAlert(e) {
      let status = e && e.response ? e.response.status : null;
      let message = e && e.response && e.response.data ? e.response.data.message : null;
      if (status === 403)
        message = 'Управлять ссылками для отзывов может только администратор';
      else if (status === 404)
        message = 'Специалист или ссылка не найдены';
      else if (this.$tools.isEmpty(message))
        message = 'Не удалось выполнить запрос';
      this.$store.commit('alertError', message);
    },
  },
}
</script>
